import React, {
  forwardRef,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import "./Select.css";
import { ChevronRotateSvg } from "../../assets/icons/set/ChevronRotateSvg";

interface SelectOption {
  value: string;
  label: string;
  disabled?: boolean;
}

interface SelectProps extends React.ComponentPropsWithoutRef<"select"> {
  options: SelectOption[];
  testId?: string;
}

const Select = forwardRef<HTMLSelectElement, SelectProps>(
  (
    {
      className = "",
      options,
      testId,
      value,
      onChange,
      onBlur,
      disabled = false,
      ...otherProps
    },
    ref
  ) => {
    const [open, setOpen] = useState(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const findIndex = useCallback(
      (value) => {
        return options.findIndex((item) => item.value === value);
      },
      [options]
    );
    const initialIndex = findIndex(value);
    const [selectedIndex, setSelectedIndex] = useState(
      initialIndex > -1 ? initialIndex : 0
    );
    const [isFocus, setIsFocus] = useState(false);

    const selectRef = useRef<HTMLDivElement>(null);
    const localRef = useRef<HTMLSelectElement>(null);

    useEffect(() => {
      const handleClickOutside = (event: FocusEvent) => {
        if (
          open &&
          selectRef.current &&
          !selectRef.current.contains(event.target as Node)
        ) {
          setOpen(false);
        }
      };

      document.addEventListener("click", (e) => handleClickOutside(e));
      return () => {
        document.removeEventListener("click", handleClickOutside);
      };
    }, [open, selectRef]);

    useEffect(() => {
      const newIndex = findIndex(value);
      if (newIndex && newIndex > -1) {
        setSelectedIndex(newIndex);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [value, options]);

    const toggleOpen = () => {
      if (disabled) {
        return;
      }
      setOpen(!open);
    };

    const handleOptionClick = (e: React.ChangeEvent<HTMLSelectElement>) => {
      setSelectedIndex(findIndex(e.target.value)); // store index
      onChange?.(e);
    };

    const handleCustomClick = (index: number) => {
      // trigger onChange event for native select
      const nativeSelectValueSetter = Object.getOwnPropertyDescriptor(
        window.HTMLSelectElement.prototype,
        "value"
      ).set;
      nativeSelectValueSetter.call(localRef.current, options[index].value);

      const ev = new Event("change", { bubbles: true });

      localRef.current.dispatchEvent(ev);

      // trigger onBlur event for native select
      localRef.current.focus();
      localRef.current.blur();

      // close custom select
      setOpen(false);
    };

    useEffect(() => {
      if (selectedIndex >= options.length) {
        setSelectedIndex(0);
      }
    }, [options, selectedIndex]);

    return (
      <div
        className={`sr-select-container ${
          disabled ? "disabled-select" : ""
        } ${className}`}
        ref={selectRef}
      >
        <select
          ref={(e) => {
            localRef.current = e;
            return ref;
          }}
          {...otherProps}
          value={options[selectedIndex]?.value}
          onChange={(e) => handleOptionClick(e)}
          onFocus={() => setIsFocus(true)}
          onBlur={(e) => {
            setIsFocus(false);
            onBlur?.(e);
          }}
          disabled={disabled}
        >
          {options.map(({ value: v, label, disabled: d }) => (
            <option key={`hidden-${v}`} value={v} disabled={d}>
              {label}
            </option>
          ))}
        </select>

        <button
          type="button"
          className={`sr-select-button ${isFocus ? "sr-focus" : ""}`}
          onClick={toggleOpen}
          aria-hidden="true"
          tabIndex={-1}
          data-testid={testId}
        >
          {options[selectedIndex]?.label}
          {!(disabled === true) ? (
            <ChevronRotateSvg
              width={20}
              height={12}
              className={`chevron ${open ? "rotated" : ""}`}
            />
          ) : null}
        </button>
        {open && (
          <ul className="sr-option-list" aria-hidden="true">
            {options.map(({ value, label, disabled: d }, index) => (
              // eslint-disable-next-line jsx-a11y/click-events-have-key-events,jsx-a11y/no-noninteractive-element-interactions
              <li
                key={value}
                className={`sr-option ${d ? "sr-hidden-option" : ""}`}
                onClick={() => (d ? null : handleCustomClick(index))}
              >
                {label}
              </li>
            ))}
          </ul>
        )}
      </div>
    );
  }
);

export { Select };
export type { SelectOption, SelectProps };
